import {
  Button, Card, DatePicker, Input, Select, Space, Table, Tag, Typography,
} from 'antd';
import rideTripApi from 'api/rideTripApi';
import ToastHelper from 'general/helpers/ToastHelper';
import useRouter from 'hooks/useRouter';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  formatTripDate, formatTripMoney, getRideTripErrorMessage, RideTripStatusTag, STATUS_OPTIONS,
} from '../../rideTripsConstants';

const { Text, Title } = Typography;

const EMPTY_CRITERIA = {
  status: '', tripCode: '', driver: '', plate: '', departureFrom: '', departureTo: '',
};

function RideTripsListScreen() {
  const router = useRouter();
  const [filter, setFilter] = useState({ status: '', page: 1, pageSize: 20 });
  const [criteria, setCriteria] = useState(EMPTY_CRITERIA);
  const [departureRange, setDepartureRange] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await rideTripApi.getTrips(filter);
      setItems(response?.items || []);
      setTotal(response?.total || 0);
    } catch (error) {
      setItems([]);
      setTotal(0);
      ToastHelper.showError(getRideTripErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const applyCriteria = () => setFilter((value) => ({ ...value, ...criteria, page: 1 }));
  const resetCriteria = () => {
    setCriteria(EMPTY_CRITERIA);
    setDepartureRange(null);
    setFilter((value) => ({ status: '', page: 1, pageSize: value.pageSize }));
  };

  const goToDetail = (tripId) => router.navigate(`${tripId}`);

  const columns = useMemo(() => [
    { title: 'Mã chuyến', dataIndex: 'tripCode', render: (value) => <Text strong>{value}</Text> },
    {
      title: 'Tài xế',
      render: (_, row) => (
        <div>
          <Text strong>{row.driver?.fullname || `User #${row.driverId}`}</Text>
          <br />
          <Text type="secondary">{row.driver?.phone || row.driver?.email || ''}</Text>
        </div>
      ),
    },
    { title: 'Phương tiện', render: (_, row) => row.vehicle?.plate || `#${row.vehicleId}` },
    {
      title: 'Tuyến',
      render: (_, row) => (
        <div>
          {row.originAddress || '—'}
          <br />
          <Text type="secondary">
            →
            {' '}
            {row.destinationAddress || '—'}
          </Text>
        </div>
      ),
    },
    { title: 'Khởi hành', dataIndex: 'departureAt', render: formatTripDate },
    { title: 'Ghế', render: (_, row) => (row.isCharter ? <Tag color="gold">Bao xe {row.charterVehicleCapacity || 4} chỗ</Tag> : `${row.seatsConfirmed || 0}/${row.seatsOffered || 0}`) },
    { title: 'Giá', dataIndex: 'pricePerSeat', render: (value, row) => (row.isCharter ? `${formatTripMoney(row.charterTotalFare)} / xe` : formatTripMoney(value)) },
    { title: 'Trạng thái', dataIndex: 'status', render: (value) => <RideTripStatusTag status={value} /> },
    { title: '', render: (_, row) => <Button type="link" onClick={() => goToDetail(row.tripId)}>Chi tiết</Button> },
  ], []);

  return (
    <div className="container-fluid px-4 my-4">
      <Card>
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
          <div>
            <Title level={4} className="mb-0">Chuyến xe ghép</Title>
            <Text type="secondary">
              Tổng
              {' '}
              {total}
              {' '}
              chuyến
            </Text>
          </div>
          <Button onClick={load} loading={loading}>Làm mới</Button>
        </div>
        <Space wrap size={[12, 12]} style={{ marginBottom: 20 }}>
          <Input
            value={criteria.tripCode}
            placeholder="Mã chuyến"
            allowClear
            style={{ width: 180 }}
            onChange={(event) => setCriteria((value) => ({ ...value, tripCode: event.target.value }))}
            onPressEnter={applyCriteria}
          />
          <Input
            value={criteria.driver}
            placeholder="Tên, email hoặc SĐT tài xế"
            allowClear
            style={{ width: 240 }}
            onChange={(event) => setCriteria((value) => ({ ...value, driver: event.target.value }))}
            onPressEnter={applyCriteria}
          />
          <Input
            value={criteria.plate}
            placeholder="Biển số xe"
            allowClear
            style={{ width: 160 }}
            onChange={(event) => setCriteria((value) => ({ ...value, plate: event.target.value }))}
            onPressEnter={applyCriteria}
          />
          <Select
            value={criteria.status}
            options={STATUS_OPTIONS}
            style={{ width: 190 }}
            onChange={(status) => setCriteria((value) => ({ ...value, status }))}
          />
          <DatePicker.RangePicker
            value={departureRange}
            format="DD/MM/YYYY"
            placeholder={['Khởi hành từ', 'Đến ngày']}
            onChange={(dates) => {
              setDepartureRange(dates);
              setCriteria((value) => ({
                ...value,
                departureFrom: dates?.[0]?.startOf('day').toISOString() || '',
                departureTo: dates?.[1]?.endOf('day').toISOString() || '',
              }));
            }}
          />
          <Button type="primary" style={{ backgroundColor: '#30BD6F', borderColor: '#30BD6F' }} onClick={applyCriteria}>Lọc</Button>
          <Button onClick={resetCriteria}>Xóa lọc</Button>
        </Space>
        <Table
          rowKey="tripId"
          dataSource={items}
          columns={columns}
          loading={loading}
          scroll={{ x: 1100 }}
          locale={{ emptyText: 'Không có dữ liệu' }}
          pagination={{
            current: filter.page,
            pageSize: filter.pageSize,
            total,
            showSizeChanger: true,
            onChange: (page, pageSize) => setFilter((value) => ({ ...value, page, pageSize })),
          }}
          onRow={(row) => ({ onDoubleClick: () => goToDetail(row.tripId) })}
        />
      </Card>
    </div>
  );
}

export default RideTripsListScreen;
